import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { ApiError, get, type UpgradeInfo } from './api';

export type Product = 'buzzbee' | 'receptionistbee';

export type WorkspaceSummary = {
  id: string;
  name: string;
  slug: string;
  role: 'owner' | 'admin' | 'staff';
  vertical: string;
  timezone: string;
  brand: { product: Product; name: string; showPoweredBy: boolean };
  entitlements: {
    plan: string;
    planName: string;
    product: Product;
    productName: string;
    state: 'trialing' | 'active' | 'past_due' | 'inactive' | 'suspended';
    active: boolean;
    features: string[];
    limits: { voiceMinutes: number | null; locations: number | null; phoneNumbers: number | null };
    trialEndsAt: string | null;
    trialDaysLeft: number | null;
    managedBy: 'hive' | 'standalone';
  };
  locked: UpgradeInfo[];
  onboarding: { step: number; completed: boolean };
  agent: { paused: boolean; pauseReason: string | null; widgetKey: string | null };
  managedBy: 'hive' | 'standalone';
  hive: { billingUrl: string | null; appsUrl: string | null } | null;
};

export type Me = {
  user: { id: string; email: string; name: string };
  workspaces: { id: string; name: string; role: string; product: Product }[];
  current: WorkspaceSummary | null;
  hive: { enabled: boolean; webUrl: string | null };
};

export function useMe() {
  return useQuery<Me | null>({
    queryKey: ['me'],
    queryFn: async () => {
      try {
        return await get<Me>('/api/me');
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 30_000,
  });
}

export function useWorkspace(): WorkspaceSummary {
  const me = useMe();
  if (!me.data?.current) throw new Error('useWorkspace outside a signed-in workspace');
  return me.data.current;
}

export function useRefreshMe() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries();
}

export function hasFeature(ws: WorkspaceSummary, feature: string): boolean {
  return ws.entitlements.features.includes(feature);
}

/** Switch the page palette and title to the workspace's product (BuzzBee or ReceptionistBee). */
export function useBrand(product: Product | null) {
  useEffect(() => {
    const p = product ?? 'buzzbee';
    document.documentElement.setAttribute('data-brand', p);
    document.title = p === 'receptionistbee' ? 'ReceptionistBee' : 'BuzzBee';
    try {
      localStorage.setItem('bz-brand', p);
    } catch {
      // private mode
    }
  }, [product]);
}
